<template>
    <IOContainer :maxHeight="maxHeight">
        <div v-if="containsFiles">
            <v-table v-if="!loading">
                <thead>
                    <tr class="text-body-medium font-weight-medium">
                        <th class="text-left">Output</th>
                        <th class="text-left">File Name</th>
                        <th class="text-right">Size</th>
                        <th class="text-center">Download</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="output in outputs" :key="output.name">
                        <td class="text-left">{{ output.name }}</td>
                        <td class="text-left">{{ output.fileName }}</td>
                        <td class="text-right">{{ output.size }}</td>
                        <td class="text-center">
                            <v-btn
                                variant="text"
                                @click="
                                    downloadFile(
                                        output.downloadUrl,
                                        output.fileName
                                    )
                                "
                            >
                                <v-icon size="x-large"> mdi-download </v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-table>
            <div v-else>loading...</div>
        </div>
        <div v-else class="text-body-large">No file outputs</div>
    </IOContainer>
</template>
<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import IOContainer from '../shared/IOContainer.vue'

const store = useStore()
const props = defineProps({
    record: {
        type: Object,
        required: true,
    },
    maxHeight: {
        type: String,
        default: '100%',
    },
})

const loading = ref(true)
const outputs = ref([])

watch(
    () => props.record,
    async () => {
        loading.value = true
        await setOutputs()
    },
    { deep: true }
)

const containsFiles = computed(() => {
    return (
        (props.record.output.result && props.record.calc.hasFileOutputs) ||
        props.record.output.arupComputeResultFileItems
    )
})

async function setOutputs() {
    if (props.record.output.result && props.record.calc.hasFileOutputs) {
        outputs.value = [
            await pullInfoFromUrl(props.record.output.result, 'Default'),
        ]
        loading.value = false
        return
    }
    if (props.record.output.arupComputeResultFileItems) {
        outputs.value = await Promise.all(
            props.record.output.arupComputeResultFileItems.map((item) =>
                pullInfoFromUrl(item.fileLink, item.description)
            )
        )
        loading.value = false
        return
    }
    loading.value = false
}

async function pullInfoFromUrl(url, outputName) {
    const pathSplit = new URL(url).pathname.split('/')
    const fileSizeBytes = await store.dispatch('getBlobSize', { blobUrl: url })
    return {
        name: outputName,
        fileName: pathSplit[pathSplit.length - 1].replaceAll('%20', ' '),
        size: humanFileSize(fileSizeBytes),
        downloadUrl: url,
    }
}

function downloadFile(uri, name) {
    let link = document.createElement('a')
    link.download = name
    link.href = uri
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

function humanFileSize(bytes, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
    }
    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
    let u = -1
    const r = 10 ** dp
    do {
        bytes /= thresh
        ++u
    } while (
        Math.round(Math.abs(bytes) * r) / r >= thresh &&
        u < units.length - 1
    )
    return bytes.toFixed(dp) + ' ' + units[u]
}

onMounted(async () => {
    await setOutputs()
})
</script>
<style scoped>
th {
    opacity: 0.8;
}
</style>
